(() => {
  // app/scripts/search.js
  if (!customElements.get("search-form")) {
    class SearchForm extends HTMLElement {
      constructor() {
        super();
        this.elms = {
          formContainer: this.querySelector("form[action*='/search']"),
          inputElement: this.querySelector("input[name='q']"),
          resultsContainer: this.querySelector(".js-results-container"),
          viewAllButton: this.querySelector(".js-view-all"),
          closeButton: this.querySelector(".icon-close")
        };
        this.bindEvents();
      }
      bindEvents() {
        const {
          inputElement,
          formContainer,
          viewAllButton,
          closeButton
        } = this.elms;
        const debouncedOnChange = AT.debounce((event) => {
          this.onChange(event);
        }, 500);
        inputElement.addEventListener("input", debouncedOnChange.bind(this));
        formContainer.addEventListener("submit", this.onFormSubmit.bind(this));
        if (viewAllButton) {
          viewAllButton.addEventListener("click", () => formContainer.submit());
        }
        closeButton.addEventListener("click", this.onCloseClick.bind(this));
        inputElement.addEventListener("focus", this.onFocus.bind(this));
        this.addEventListener("focusout", this.onFocusOut.bind(this));
        this.addEventListener("keyup", this.onKeyup.bind(this));
        this.addEventListener("keydown", this.onKeydown.bind(this));
      }
      onKeyup(event) {
        if (!this.getQuery().length)
          return;
        event.preventDefault();
        switch (event.code) {
          case "ArrowUp":
            this.switchOption("up");
            break;
          case "ArrowDown":
            this.switchOption("down");
            break;
          case "Enter":
            this.selectOption();
            break;
        }
      }
      onKeydown(event) {
        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
          event.preventDefault();
        }
      }
      switchOption(direction) {
        if (!this.classList.contains("is-visible") || this.dataset.status == "empty" || this.dataset.status == "loading")
          return;
        const moveUp = direction === "up";
        const selectedElement = this.querySelector('[aria-selected="true"]');
        const allVisibleElements = Array.from(this.querySelectorAll("li, button.js-view-all")).filter((element) => element.offsetParent !== null);
        let activeElementIndex = 0;
        if (moveUp && !selectedElement)
          return;
        let selectedElementIndex = -1;
        let i = 0;
        while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
          if (allVisibleElements[i] === selectedElement) {
            selectedElementIndex = i;
          }
          i++;
        }
        this.statusElement.textContent = "";
        if (!moveUp && selectedElement) {
          activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
        } else if (moveUp) {
          activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
        }
        if (activeElementIndex === selectedElementIndex)
          return;
        const activeElement = allVisibleElements[activeElementIndex];
        activeElement.setAttribute("aria-selected", true);
        activeElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
        if (selectedElement)
          selectedElement.setAttribute("aria-selected", false);
        if (activeElement.id) {
          this.elms.inputElement.setAttribute("aria-activedescendant", activeElement.id);
        }
      }
      selectOption() {
        const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
        if (selectedOption)
          selectedOption.click();
      }
      onFocus() {
        this.parentElement?.classList.add("show-search");
        AT.disableScroll();
        const currentSearchTerm = this.getQuery();
        if (!currentSearchTerm.length)
          return;
        if (this.getAttribute("data-status") === "show" || this.getAttribute("data-status") === "empty") {
          this.open();
        }
      }
      onFocusOut(event) {
        setTimeout(() => {
          if (this.contains(document.activeElement)) {
            return;
          }
          this.parentElement?.classList.remove("show-search");
          this.close();
          AT.enableScroll();
        }, 0);
      }
      onCloseClick() {
        this.parentElement?.classList.remove("show-search");
        this.close();
        AT.enableScroll();
        this.elms.inputElement.blur();
      }
      onFormSubmit(event) {
        if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a'))
          event.preventDefault();
      }
      onChange(event) {
        const newSearchTerm = this.getQuery();
        if (!newSearchTerm.length) {
          this.close(true);
          return;
        }
        this.getSearchResults(newSearchTerm);
      }
      getQuery() {
        return this.elms.inputElement.value.trim();
      }
      getSearchResults(searchTerm) {
        this.setLiveRegionLoadingState();
        fetch(`${theme.routes.predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`).then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        }).then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(responseText, "text/html").querySelector("#shopify-section-predictive-search");
          this.renderSearchResults(parsedHTML);
        }).catch((error) => {
          this.close();
          throw error;
        });
      }
      renderSearchResults(parsedHTML) {
        this.elms.resultsContainer.innerHTML = parsedHTML.innerHTML;
        this.setLiveRegionResults();
        this.open();
      }
      setLiveRegionResults() {
        if (!!this.elms.resultsContainer.querySelector(".groups .group")) {
          this.setStatus("show");
        } else {
          this.setStatus("empty");
        }
        this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
      }
      setLiveRegionLoadingState() {
        this.statusElement = this.statusElement || this.querySelector(".search-results_status");
        this.loadingText = this.loadingText || this.statusElement.getAttribute("data-loading-text");
        this.setStatus("visible");
        this.setStatus("loading");
        this.setLiveRegionText(this.loadingText);
      }
      setLiveRegionText(statusText) {
        this.statusElement.setAttribute("aria-hidden", "false");
        this.statusElement.textContent = statusText;
        setTimeout(() => {
          this.statusElement.setAttribute("aria-hidden", "true");
        }, 1e3);
      }
      open() {
        this.setStatus("visible");
        this.elms.inputElement.setAttribute("aria-expanded", true);
      }
      close(removeDataStatus = false) {
        if (removeDataStatus) {
          this.setStatus("");
        }
        this.setStatus("unvisible");
        this.elms.inputElement.setAttribute("aria-expanded", false);
        this.elms.inputElement.removeAttribute("aria-activedescendant");
      }
      setStatus(status) {
        switch (status) {
          case "loading":
            this.setAttribute("data-status", "loading");
            break;
          case "empty":
            this.setAttribute("data-status", "empty");
            break;
          case "visible":
            this.addClass("is-visible");
            break;
          case "unvisible":
            this.removeClass("is-visible");
            break;
          case "show":
            this.setAttribute("data-status", "show");
            break;
          default:
            this.setAttribute("data-status", "");
        }
      }
    }
    customElements.define("search-form", SearchForm);
    console.log("search.js loaded");
  }
})();
